import { Link, useNavigate } from "react-router-dom";
// import { useTheme } from "../../../context/ThemeContext";
import { Sun, Moon, Menu, X, LogOut, ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";
import { LogoWithText } from "../../Logo";

const Header = () => {
  // const { theme, toggleTheme } = useTheme();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (error) {}
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("user");
    setUser(null);
    navigate("/login");
  };

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const renderActionButton = () => {
    if (!user) {
      return (
        <div className="flex items-center gap-2">
          <Link
            to="/chat"
            className="px-4 py-2 text-fg-40 border border-bd-50 bg-bg-70 dark:bg-bg-60 hover:bg-bg-50 rounded-full text-sm font-medium transition-colors flex items-center justify-center gap-2"
          >
            Try Chat
          </Link>
          <Link
            to="/login"
            className="px-4 py-2 text-white bg-ac-02 dark:text-black hover:bg-ac-01 rounded-full text-sm font-medium transition-colors flex items-center justify-center gap-2 shadow-sm"
          >
            Get Started
            <ArrowRight size={16} />
          </Link>
        </div>
      );
    }

    // Get first letter of user's name
    const getInitial = (name) => {
      return name ? name.charAt(0).toUpperCase() : "U";
    };

    return (
      <div className="flex items-center gap-3 bg-bg-50 dark:bg-bg-40 px-1 py-1 rounded-full">
        {/* User info (Hi, Name + Avatar) */}
        <div className="flex items-center gap-2 cursor-pointer">
          <div className="w-8 h-8 rounded-full bg-ac-02 dark:bg-ac-02 flex items-center justify-center border border-bd-50 dark:border-bd-50">
            <span className="text-white dark:text-black text-sm font-semibold">
              {getInitial(user.name)}
            </span>
          </div>
          <div className="flex flex-col leading-tight">
            <span className="text-xs text-fg-60 dark:text-fg-60">Hi,</span>
            <span className="text-sm font-medium text-fg-40 dark:text-fg-40">
              {user.name || "User"}
            </span>
          </div>
        </div>

        {/* Go to respective role page */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => {
              // Handle individual users - check trial status and route accordingly
              if (user.role === "individual") {
                const status = user?.trial?.status;
                const planName = user?.plan?.name || null;

                // Route directly to the correct page based on trial/plan status
                if (
                  status === "progress" ||
                  status === "paid" ||
                  planName === "pro"
                ) {
                  navigate("/chat", { replace: true });
                } else if (status === "pending") {
                  navigate("/activate-trial", { replace: true });
                } else if (
                  status === "expired" ||
                  status === "cancelled" ||
                  status === "past_due"
                ) {
                  navigate("/upgrade-plan", { replace: true });
                } else {
                  // Default: activate trial if no status
                  navigate("/activate-trial", { replace: true });
                }
              } else if (user.role === "employee") {
                navigate("/chat", { replace: true });
              } else if (user.role === "admin" || user.role === "owner") {
                navigate("/org/dashboard", { replace: true });
              } else if (
                user.role === "super_admin" ||
                user.role === "super admin"
              ) {
                navigate("/admin", { replace: true });
              } else {
                // Fallback to login for unknown roles
                navigate("/login", { replace: true });
              }
            }}
            className="px-4 py-2 text-white bg-ac-02 dark:text-black hover:bg-ac-01 rounded-full text-sm font-medium transition-colors flex items-center justify-center gap-2 shadow-sm"
          >
            Go
            <ArrowRight size={16} />
          </button>

          {/* Logout button (desktop) */}
          <button
            onClick={handleLogout}
            className="p-2 rounded-full text-fg-60 hover:bg-bg-60 dark:hover:bg-bg-50 transition-colors"
            title="Logout"
            aria-label="Logout"
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <header
      className="fixed top-4 left-1/2 -translate-x-1/2 z-50
      bg-bg-70/90 dark:bg-bg-60/90 
      backdrop-blur-xl border border-bd-50 dark:border-bd-50
      rounded-full w-[90%] max-w-6xl px-6 py-2 pr-4 flex items-center justify-between shadow-md transition-all"
    >
      {/* Left: Logo */}
      <div className="flex items-center">
        <Link to="/" className="flex items-center">
          <LogoWithText
            className="h-7 w-auto max-w-32"
            fillClassName="fill-fg-40 hover:fill-fg-30 transition-colors"
          />
        </Link>
      </div>

      {/* Center: Nav Links */}
      <nav className="hidden md:flex items-center space-x-6 text-sm font-medium">
        <Link
          to="/chat"
          className="text-fg-60 hover:text-ac-02 transition-colors"
        >
          Try Chat
        </Link>
        <a
          href="#features"
          className="text-fg-60 hover:text-ac-02 transition-colors"
          onClick={(e) => {
            e.preventDefault();
            document
              .getElementById("features")
              ?.scrollIntoView({ behavior: "smooth" });
          }}
        >
          Features
        </a>
        <a
          href="#plans"
          className="text-fg-60 hover:text-ac-02 transition-colors"
          onClick={(e) => {
            e.preventDefault();
            document
              .getElementById("plans")
              ?.scrollIntoView({ behavior: "smooth" });
          }}
        >
          Pricing
        </a>
        {/* This adds Testimonials nav and smooth scroll to its section */}
        <a
          href="#testimonials"
          className="text-fg-60 hover:text-ac-02 transition-colors"
          onClick={(e) => {
            e.preventDefault();
            document
              .getElementById("testimonials")
              ?.scrollIntoView({ behavior: "smooth" });
          }}
        >
          Testimonials
        </a>
        {/* This adds FAQ nav and smooth scroll to its section */}
        <a
          href="#faq"
          className="text-fg-60 hover:text-ac-02 transition-colors"
          onClick={(e) => {
            e.preventDefault();
            document
              .getElementById("faq")
              ?.scrollIntoView({ behavior: "smooth" });
          }}
        >
          FAQ's
        </a>
        <Link
          to="/contact"
          className="text-fg-60 hover:text-ac-02 transition-colors"
        >
          Contact
        </Link>
      </nav>

      {/* Right: Action Buttons */}
      <div className="hidden md:flex items-center space-x-4">
        {renderActionButton()}
        {/* TEMPORARILY COMMENTED OUT - Theme toggle hidden for landing/auth pages */}
        {/* <button
          onClick={toggleTheme}
          className="p-2 rounded-full text-fg-60 hover:bg-bg-60 transition-colors"
          aria-label="Toggle theme"
        >
          {theme === "light" ? <Moon size={20} /> : <Sun size={20} />}
        </button> */}
      </div>

      {/* Mobile menu toggle */}
      <div className="flex md:hidden items-center space-x-2">
        {/* TEMPORARILY COMMENTED OUT - Theme toggle hidden for landing/auth pages */}
        {/* <button
          onClick={toggleTheme}
          className="p-2 rounded-full text-fg-60 hover:bg-bg-60 transition-colors"
          aria-label="Toggle theme"
        >
          {theme === "light" ? <Moon size={20} /> : <Sun size={20} />}
        </button> */}
        <button
          onClick={toggleMenu}
          className="p-2 rounded-full text-fg-60 hover:bg-bg-60 transition-colors"
          aria-label="Toggle menu"
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile dropdown menu */}
      {isMenuOpen && (
        <div
          className="absolute top-16 left-0 w-full bg-bg-70 dark:bg-bg-60 backdrop-blur-xl
          rounded-2xl shadow-lg border border-bd-50 dark:border-bd-50
          py-4 px-6 flex flex-col space-y-3 md:hidden animate-fadeIn"
        >
          <a
            href="#features"
            className="text-fg-60 hover:text-ac-02 transition-colors"
            onClick={(e) => {
              e.preventDefault();
              document
                .getElementById("features")
                ?.scrollIntoView({ behavior: "smooth" });
              setIsMenuOpen(false);
            }}
          >
            Features
          </a>
          <a
            href="#plans"
            className="text-fg-60 hover:text-ac-02 transition-colors"
            onClick={(e) => {
              e.preventDefault();
              document
                .getElementById("plans")
                ?.scrollIntoView({ behavior: "smooth" });
              setIsMenuOpen(false);
            }}
          >
            Pricing
          </a>
          <Link
            to="/contact"
            className="text-fg-60 hover:text-ac-02 transition-colors"
            onClick={() => setIsMenuOpen(false)}
          >
            Contact
          </Link>
          <Link
            to="/chat"
            className="text-fg-60 hover:text-ac-02 transition-colors"
            onClick={() => setIsMenuOpen(false)}
          >
            Try Chat
          </Link>

          <div className="border-t border-bd-50 dark:border-bd-50 pt-3">
            {renderActionButton()}
          </div>

          {user && (
            <button
              onClick={() => {
                handleLogout();
                setIsMenuOpen(false);
              }}
              className="flex items-center justify-center gap-2 text-fg-60 hover:text-ac-02 transition-colors text-sm font-medium"
            >
              <LogOut size={16} />
              Logout
            </button>
          )}
        </div>
      )}
    </header>
  );
};

export default Header;
